import Icon from '@rio-cloud/rio-uikit/components/icon/Icon';
import { type MouseEvent, useCallback } from 'react';

import { useAppDispatch } from '@/configuration/setup/hooks';
import { openDocument } from '@/store/documentViewer/documentViewerSlice';
import { DOCUMENT_VIEWER_ELEMENT_ID, DOCUMENT_VIEWER_MIN_VIEWPORT_WIDTH } from '@/utils/constants';

import type { TSourceLinkProps } from './types';
import { toSourceElementId } from './utils';

// INFO: Mouse left click button
const PRIMARY_BUTTON = 0;

// INFO: Decides if the browser should handle the click. Other than the click itself it checks the min width to render the in-app viewer
const isBrowserDefaultClick = (event: MouseEvent<HTMLAnchorElement>) =>
  event.button !== PRIMARY_BUTTON ||
  event.ctrlKey ||
  event.metaKey ||
  event.shiftKey ||
  event.altKey ||
  window.innerWidth < DOCUMENT_VIEWER_MIN_VIEWPORT_WIDTH;

const SourceLink = ({ href, messageId, offset, source, children }: TSourceLinkProps) => {
  const dispatch = useAppDispatch();
  const elementId = toSourceElementId(messageId, href, offset);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (!source || isBrowserDefaultClick(event)) return;

      event.preventDefault();
      dispatch(openDocument({ source, sourceElementId: elementId }));
    },
    [dispatch, source, elementId],
  );

  return (
    <span className="text-capitalize">
      <a
        id={source ? elementId : undefined}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-color-primary"
        aria-controls={source ? DOCUMENT_VIEWER_ELEMENT_ID : undefined}
        onClick={source ? handleClick : undefined}
      >
        <Icon iconName="icon-files" aria-hidden="true" />
        <span className="text-size-12-xs text-size-14-ls">{children}</span>
      </a>
    </span>
  );
};

export default SourceLink;
